interface ZodiacWheelProps {
  sunSign: string;
}

export default function ZodiacWheel({ sunSign }: ZodiacWheelProps) {
  const zodiacSigns = [
    { name: '양자리', icon: 'ri-contrast-2-line', color: '#ef4444', angle: 0 },
    { name: '황소자리', icon: 'ri-contrast-2-line', color: '#10b981', angle: 30 },
    { name: '쌍둥이자리', icon: 'ri-contrast-2-line', color: '#f59e0b', angle: 60 },
    { name: '게자리', icon: 'ri-contrast-2-line', color: '#3b82f6', angle: 90 },
    { name: '사자자리', icon: 'ri-sun-line', color: '#f59e0b', angle: 120 },
    { name: '처녀자리', icon: 'ri-leaf-line', color: '#10b981', angle: 150 },
    { name: '천칭자리', icon: 'ri-scales-3-line', color: '#ec4899', angle: 180 },
    { name: '전갈자리', icon: 'ri-bug-line', color: '#8b5cf6', angle: 210 },
    { name: '사수자리', icon: 'ri-arrow-right-up-line', color: '#ef4444', angle: 240 },
    { name: '염소자리', icon: 'ri-mountain-line', color: '#6366f1', angle: 270 },
    { name: '물병자리', icon: 'ri-water-flash-line', color: '#06b6d4', angle: 300 },
    { name: '물고기자리', icon: 'ri-water-flash-line', color: '#8b5cf6', angle: 330 },
  ];

  const centerX = 200;
  const centerY = 200;
  const outerRadius = 160;
  const innerRadius = 100;

  return (
    <div className="flex justify-center items-center">
      <svg width="400" height="400" className="drop-shadow-2xl">
        <defs>
          {/* 그라디언트 정의 */}
          <radialGradient id="wheelGradient">
            <stop offset="0%" stopColor="rgba(251, 191, 36, 0.2)" />
            <stop offset="50%" stopColor="rgba(168, 85, 247, 0.1)" />
            <stop offset="100%" stopColor="rgba(59, 130, 246, 0.05)" />
          </radialGradient>
          
          {/* 각 별자리별 그라디언트 */}
          {zodiacSigns.map((sign, i) => (
            <linearGradient key={i} id={`signGradient-${i}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={sign.color} stopOpacity="0.6" />
              <stop offset="100%" stopColor={sign.color} stopOpacity="0.2" />
            </linearGradient>
          ))}

          {/* 글로우 필터 */}
          <filter id="glow">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* 배경 원들 */}
        <circle cx={centerX} cy={centerY} r={outerRadius} fill="url(#wheelGradient)" opacity="0.3" />
        <circle cx={centerX} cy={centerY} r={outerRadius} fill="none" stroke="rgba(251, 191, 36, 0.3)" strokeWidth="2" />
        <circle cx={centerX} cy={centerY} r={innerRadius} fill="none" stroke="rgba(251, 191, 36, 0.2)" strokeWidth="1.5" strokeDasharray="5 5" />
        
        {/* 중앙 원 */}
        <circle cx={centerX} cy={centerY} r={innerRadius - 10} fill="rgba(0, 0, 0, 0.3)" />
        <circle cx={centerX} cy={centerY} r={innerRadius - 10} fill="none" stroke="rgba(251, 191, 36, 0.4)" strokeWidth="2" />

        {/* 별자리 섹션 */}
        {zodiacSigns.map((sign, index) => {
          const startAngle = (sign.angle - 15) * (Math.PI / 180);
          const endAngle = (sign.angle + 15) * (Math.PI / 180);
          const isActive = sign.name === sunSign;

          // 섹션 경로 생성
          const outerStartX = centerX + outerRadius * Math.cos(startAngle);
          const outerStartY = centerY + outerRadius * Math.sin(startAngle);
          const outerEndX = centerX + outerRadius * Math.cos(endAngle);
          const outerEndY = centerY + outerRadius * Math.sin(endAngle);
          const innerStartX = centerX + innerRadius * Math.cos(startAngle);
          const innerStartY = centerY + innerRadius * Math.sin(startAngle);
          const innerEndX = centerX + innerRadius * Math.cos(endAngle);
          const innerEndY = centerY + innerRadius * Math.sin(endAngle);

          const pathData = `
            M ${outerStartX} ${outerStartY}
            A ${outerRadius} ${outerRadius} 0 0 1 ${outerEndX} ${outerEndY}
            L ${innerEndX} ${innerEndY}
            A ${innerRadius} ${innerRadius} 0 0 0 ${innerStartX} ${innerStartY}
            Z
          `;

          // 아이콘 위치
          const iconRadius = (outerRadius + innerRadius) / 2;
          const iconAngle = sign.angle * (Math.PI / 180);
          const iconX = centerX + iconRadius * Math.cos(iconAngle);
          const iconY = centerY + iconRadius * Math.sin(iconAngle);

          return (
            <g key={index}>
              {/* 섹션 배경 */}
              <path
                d={pathData}
                fill={isActive ? sign.color : `url(#signGradient-${index})`}
                opacity={isActive ? '0.8' : '0.3'}
                stroke="rgba(255, 255, 255, 0.2)"
                strokeWidth="1"
                className="transition-all duration-300 hover:opacity-70 cursor-pointer"
                filter={isActive ? 'url(#glow)' : ''}
              />

              {/* 활성 별자리 강조 */}
              {isActive && (
                <>
                  <path
                    d={pathData}
                    fill="none"
                    stroke={sign.color}
                    strokeWidth="3"
                    opacity="0.8"
                  />
                  {/* 펄스 애니메이션 */}
                  <circle cx={iconX} cy={iconY} r="25" fill={sign.color} opacity="0.2">
                    <animate attributeName="r" from="25" to="35" dur="2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.2" to="0" dur="2s" repeatCount="indefinite" />
                  </circle>
                </>
              )}

              {/* 아이콘 배경 */}
              <circle
                cx={iconX}
                cy={iconY}
                r="18"
                fill={isActive ? sign.color : 'rgba(255, 255, 255, 0.1)'}
                opacity={isActive ? '0.9' : '0.5'}
              />

              {/* 별자리 이름 (외부) */}
              <text
                x={centerX + (outerRadius + 25) * Math.cos(iconAngle)}
                y={centerY + (outerRadius + 25) * Math.sin(iconAngle)}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={isActive ? sign.color : 'rgba(255, 255, 255, 0.6)'}
                fontSize={isActive ? '14' : '12'}
                fontWeight={isActive ? 'bold' : 'normal'}
                className="transition-all"
              >
                {sign.name}
              </text>
            </g>
          );
        })}

        {/* 중앙 텍스트 */}
        <text
          x={centerX}
          y={centerY - 10}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="rgba(251, 191, 36, 0.8)"
          fontSize="16"
          fontWeight="bold"
        >
          {sunSign}
        </text>
        <text
          x={centerX}
          y={centerY + 15}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="rgba(255, 255, 255, 0.6)"
          fontSize="12"
        >
          Sun Sign
        </text>

        {/* 장식 별들 */}
        {[...Array(8)].map((_, i) => {
          const angle = (i * 45) * (Math.PI / 180);
          const radius = outerRadius + 15;
          const x = centerX + radius * Math.cos(angle);
          const y = centerY + radius * Math.sin(angle);
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="2"
              fill="rgba(251, 191, 36, 0.6)"
            >
              <animate attributeName="opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite" begin={`${i * 0.3}s`} />
            </circle>
          );
        })}
      </svg>
    </div>
  );
}